import { normalizeImportTable, type ImportCell } from "./import";
import { MAX_ELAPSED_MS, type Sample, type TemperatureUnit } from "./index";

/** Text positions from PDF.js, in unrotated PDF page coordinates. */
export type PdfText = { text: string; x: number; y: number; width: number };
export type CropsterPdfCurve = {
  samples: Sample[];
  events: { elapsedMs: number; text: string; note: string }[];
  unit: TemperatureUnit;
  intervalMs: { min: number; max: number };
  hasEnvironment: boolean;
};
export const MAX_ROAST_PDF_PAGES = 60;
export const MAX_ROAST_PDF_ITEMS = 100_000;
export const MAX_ROAST_PDF_TEXT = 2_000_000;

const timePattern = /^(?:\d{1,3}:)?\d{1,3}:[0-5]\d$/;
const unsupported = () => new Error("Use a single English Cropster ‘Print full details’ PDF with its time/temperature readings table. A graph or scanned image alone cannot supply measured readings.");
function time(text: string): number {
  if (!timePattern.test(text)) throw new Error(`Invalid PDF elapsed time “${text}”. Use a report with elapsed mm:ss times.`);
  const parts = text.split(":").map(Number);
  if (parts.length === 3 && parts[1]! >= 60) throw new Error("Invalid minutes in PDF elapsed time.");
  const ms = parts.reduce((total, value) => total * 60 + value, 0) * 1000;
  if (ms > MAX_ELAPSED_MS) throw new Error("PDF readings must be within four hours of charge.");
  return ms;
}

function lines(items: readonly PdfText[]): PdfText[][] {
  const ordered = items.filter(item => item.text.trim()).map(item => {
    if (![item.x, item.y, item.width].every(Number.isFinite) || item.width < 0 || item.text.length > 10_000) throw unsupported();
    return { ...item, text: item.text.replace(/\s+/g, " ").trim() };
  }).sort((a, b) => b.y - a.y || a.x - b.x);
  const result: PdfText[][] = [];
  for (const item of ordered) {
    const last = result.at(-1);
    if (last && Math.abs(last[0]!.y - item.y) <= 1.5) last.push(item);
    else result.push([item]);
  }
  return result.map(row => row.sort((a, b) => a.x - b.x));
}

const beanLabel = /^Bean (?:Te\.{3}|Te…|Temp(?:erature)?)(?:\s*\(°[CF]\))?$/i;
const environmentLabel = /^Environment(?:al)? (?:Te\.{3}|Te…|Temp(?:erature)?)(?:\s*\(°[CF]\))?$/i;

/** Columns use their printed positions, so a RoR column can never become a temperature. */
function cells(row: PdfText[], headers: PdfText[]): string[] {
  const centers = headers.map(item => item.x + item.width / 2);
  const boundaries = centers.slice(1).map((center, index) => (center + centers[index]!) / 2);
  const groups: string[][] = headers.map(() => []);
  for (const item of row) {
    const center = item.x + item.width / 2;
    const index = boundaries.findIndex(boundary => center < boundary);
    groups[index === -1 ? headers.length - 1 : index]!.push(item.text);
  }
  return groups.map(group => group.join(" "));
}

export function parseCropsterPdf(pages: readonly (readonly PdfText[])[]): CropsterPdfCurve {
  if (!pages.length || pages.length > MAX_ROAST_PDF_PAGES) throw new Error("Import a roast PDF with 1–60 pages.");
  let itemCount = 0; let textCount = 0;
  for (const page of pages) {
    itemCount += page.length;
    for (const item of page) textCount += item.text.length;
  }
  if (itemCount > MAX_ROAST_PDF_ITEMS || textCount > MAX_ROAST_PDF_TEXT) throw new Error("The PDF text is too large. Export one roast with only its details and readings.");

  const readings: ImportCell[][] = [];
  const events: CropsterPdfCurve["events"] = [];
  let unit: TemperatureUnit | undefined;
  let hasEnvironment: boolean | undefined;
  let duration: number | undefined;
  for (const [pageIndex, page] of pages.entries()) {
    const rows = lines(page);
    const footer = rows.find(row => row.some(item => /^Cropster Printout created on /.test(item.text)));
    if (!footer || !footer.some(item => item.text === `${pageIndex + 1} / ${pages.length}`)) throw unsupported();
    let headers: PdfText[] | null = null;
    let mode: "readings" | "events" | null = null;
    let bean = -1; let environment = -1;
    for (let rowIndex = 0; rowIndex < rows.length; rowIndex++) {
      const row = rows[rowIndex]!;
      if (row === footer) break;
      // A full report gives an independent end-time check, preventing partial imports.
      if (row[0]?.text === "Duration" && row.length === 2) {
        if (duration !== undefined) throw new Error("Choose a PDF for one roast, not a combined report.");
        duration = time(row[1]!.text);
      }
      if (row[0]?.text === "Time") {
        headers = row;
        if (row[1]?.text === "Event" && row[2]?.text === "Note" && row.length === 3) {
          mode = "events";
          continue;
        }
        bean = row.findIndex(item => beanLabel.test(item.text));
        environment = row.findIndex(item => environmentLabel.test(item.text));
        if (bean < 1 || row.filter(item => beanLabel.test(item.text)).length !== 1 || row.filter(item => environmentLabel.test(item.text)).length > 1) throw unsupported();
        const next = rows[rowIndex + 1];
        const unitRow = next && next.every(item => /^\(°[CF]\)$/.test(item.text)) ? cells(next, headers) : null;
        const readUnit = (index: number): TemperatureUnit => {
          const inline = /\(°([CF])\)$/.exec(row[index]!.text)?.[1];
          const separate = unitRow && /^\(°([CF])\)$/.exec(unitRow[index]!)?.[1];
          if (!inline && !separate || inline && separate && inline !== separate) throw new Error("The PDF temperature heading must explicitly identify °C or °F.");
          return (inline || separate) as TemperatureUnit;
        };
        const pageUnit = readUnit(bean);
        if (environment !== -1 && readUnit(environment) !== pageUnit || unit && unit !== pageUnit) throw new Error("PDF temperature columns and pages must use the same unit.");
        if (hasEnvironment !== undefined && hasEnvironment !== (environment !== -1)) throw new Error("The PDF temperature columns changed between pages. Export a consistent readings table.");
        unit = pageUnit;
        hasEnvironment = environment !== -1;
        if (unitRow) rowIndex++;
        mode = "readings";
        continue;
      }
      if (!mode || !headers) continue;
      const values = cells(row, headers);
      if (!values[0] || !timePattern.test(values[0])) throw new Error(`Page ${pageIndex + 1}: a row in the ${mode} table has no valid time. Wrapped or overlapping rows need a new PDF export.`);
      const elapsedMs = time(values[0]);
      if (mode === "readings") {
        readings.push([values[0], values[bean]!, environment === -1 ? null : values[environment]!]);
        if (readings.length > 20_000) throw new Error("A curve supports at most 20,000 readings.");
      } else {
        if (!values[1] || `${values[1]} ${values[2]}`.length > 495 || events.length >= 500) throw new Error("The PDF event table contains a missing label, oversized note, or more than 500 events.");
        events.push({ elapsedMs, text: values[1], note: values[2]! });
      }
    }
  }
  if (!unit || !readings.length || duration === undefined) throw unsupported();
  const { samples } = normalizeImportTable([["Time (mm:ss)", `Bean temperature (°${unit})`, `Environment temperature (°${unit})`], ...readings], {
    headerRow: 0, timeColumn: 0, beanColumn: 1, environmentColumn: hasEnvironment ? 2 : null,
    eventColumn: null, temperatureUnit: unit, timeFormat: "clock",
  });
  if (samples[0]!.elapsedMs !== 0 || samples.at(-1)!.elapsedMs !== duration) throw new Error("The PDF readings must run from 00:00 through the report’s full duration. Export the complete readings table.");
  if (events.some(event => event.elapsedMs > duration)) throw new Error("A PDF event is outside the recorded curve.");
  const intervals = samples.slice(1).map((sample, index) => sample.elapsedMs - samples[index]!.elapsedMs);
  return { samples, events, unit, hasEnvironment: Boolean(hasEnvironment), intervalMs: { min: Math.min(...intervals), max: Math.max(...intervals) } };
}
